import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

export interface AreaChartSeries<T> {
  dataKey: keyof T & string;
  label: string;
  color: string;
  fillOpacity?: number;
}

export interface AreaChartWidgetProps<T extends Record<string, unknown>> {
  data: T[];
  xKey: keyof T & string;
  series: AreaChartSeries<T>[];
  xFormatter?: (value: string) => string;
  yFormatter?: (value: number) => string;
  showGrid?: boolean;
  showLegend?: boolean;
  height?: number;
}

function CustomTooltip({ active, payload, label, xFormatter, yFormatter }: any) {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2">
      <p className="text-sm font-medium text-gray-700 mb-1">
        {xFormatter ? xFormatter(label) : label}
      </p>
      {payload.map((entry: any) => (
        <div key={entry.dataKey} className="flex items-center gap-2 text-sm">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-gray-600">{entry.name}:</span>
          <span className="font-medium text-gray-900">
            {yFormatter ? yFormatter(entry.value) : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export function AreaChartWidget<T extends Record<string, unknown>>({
  data,
  xKey,
  series,
  xFormatter,
  yFormatter,
  showGrid = true,
  showLegend = true,
  height = 280,
}: AreaChartWidgetProps<T>) {
  if (!data.length) {
    return (
      <div
        className="flex items-center justify-center text-gray-400 text-sm"
        style={{ height }}
      >
        Inte tillräckligt med data
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        {showGrid && (
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        )}
        <XAxis
          dataKey={xKey}
          tickFormatter={xFormatter}
          tick={{ fontSize: 12, fill: "#9ca3af" }}
          axisLine={{ stroke: "#e5e7eb" }}
          tickLine={false}
        />
        <YAxis
          tickFormatter={yFormatter}
          tick={{ fontSize: 12, fill: "#9ca3af" }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip
          content={
            <CustomTooltip xFormatter={xFormatter} yFormatter={yFormatter} />
          }
        />
        {showLegend && (
          <Legend
            wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
            iconType="circle"
            iconSize={8}
          />
        )}
        {series.map((s) => (
          <Area
            key={s.dataKey}
            type="monotone"
            dataKey={s.dataKey}
            name={s.label}
            stroke={s.color}
            fill={s.color}
            fillOpacity={s.fillOpacity ?? 0.1}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 0 }}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}
